import {HttpClient} from "@web-libs/core/http";
import {Information} from "@web-libs/features/information/domain/information.types";
import {ForbiddenError} from "@web-libs/core/http/api/http.errors";
import {InformationForbiddenError} from "@web-libs/features/information/domain/information.error";
import {UnexpectedDomainError} from "@web-libs/core/shared-domain/shared-domain.errors";
import {
    InformationDTO,
    parseInformationDTO,
    parseInformationPage
} from "@web-libs/features/information/api/information.types";
import {TrmzFileDTOConverter} from "@web-libs/features/files/trmz-file/api/trmz-file-dto-converter";
import {Page} from "@web-libs/core/pagination/api/pagination.functions";

/**
 * throws:
 * - InformationForbiddenError => if requested information is not accessible for the current user
 * - UnexpectedDomainError => if something unexpected happens
 */
export type InformationRepository = ReturnType<typeof createInformationRepository>

export const createInformationRepository = (httpClient: HttpClient, trmzFileConverter: TrmzFileDTOConverter) => {
    return {
        getInformation(id: string): Promise<Information> {
            return withErrorHandler(async () => {
                const response = await httpClient.get<InformationDTO>('information/' + id)
                return parseInformationDTO(response, trmzFileConverter);
            });
        },
        getInformations(instId: string): Promise<Information[]> {
            const params = {
                instIds: [instId],
                sort: 'publishDate,insertDate,desc'
            }
            return withErrorHandler(
                async () => {
                    const page = await httpClient.get<Page<InformationDTO>>('information', {
                        queryParams: params
                    })
                    return parseInformationPage(page, trmzFileConverter)
                }
            )
        },
        async getPublicInformation(id: string): Promise<Information> {
            const dto = await httpClient.get<InformationDTO>('public/information/' + id, {
                noCache: true,
            });
            return parseInformationDTO(dto, trmzFileConverter);
        },
      /**
       * Get public informations
       * @param instId
       * @param tagIdentifier
       */
        getPublicInformations: async (instId: string, tagIdentifier?: string[]) => {
          const params = {
                instIds: instId ? [instId] : [],
                tagIdentifier: tagIdentifier ? [...tagIdentifier] : [],
                sort: 'publishDate,insertDate,desc'
            }
            const page = await httpClient.get<Page<InformationDTO>>('public/information', {
                queryParams: params,
                noCache: true,
            })
            return parseInformationPage(page, trmzFileConverter)
        }
    }
}

type FetchFunction<T> = () => Promise<T>
const withErrorHandler = async <T>(fn: FetchFunction<T>) => {
    try {
        return await fn()
    } catch (e) {
        if (e instanceof ForbiddenError) {
            throw new InformationForbiddenError()
        } else {
            throw UnexpectedDomainError.fromError(e)
        }
    }
}
